/**
 * Company seed authoring - the single nested source both `tenant.seed.ts` and
 * `store.seed.ts` flatten from. A tenant is a retailer COMPANY; its physical
 * stores are authored inline underneath it (no repeated tenantId), which keeps
 * the company → stores relationship visible at authoring time.
 *
 * Business model: every company's products are ALWAYS on the global
 * marketplace (no opt-out - a deliberate deviation from HLD SM-08). A company
 * may additionally PAY for its own branded site - a subdomain-scoped
 * marketplace listing only its products - which is what `ownSiteEnabled`
 * gates. The subdomain itself is reserved for every company at onboarding.
 *
 * Derived downstream, never hand-maintained here:
 * - `stats.storeCount` / `stats.itemCount` / `stats.auctionsEndingToday`
 *   (tenant.seed.ts computes them from this roster + the listing seed)
 * - the "N locations · …" blurb prefix (from the nested store count)
 * - the tenant-band copy (from `name` + `subdomain` at render time)
 * - `hoursLabel` (store.seed.ts derives it from `weekHours` at seed time)
 *
 * Store coordinates are real-world so Haversine distances look right; phone
 * numbers use the reserved 555-01XX fictional range with real metro area
 * codes.
 */
import { StoreDayHours, StoreState, StoreWeekHours } from '@core/models/store.state';
import { TenantPolicyState, TenantThemeState } from '@core/models/tenant.state';

/** A store authored under its company - tenantId is stamped at flatten time. */
export interface CompanyStoreSeed extends Omit<StoreState, 'tenantId' | 'hoursLabel'> {
  /** Item-number prefix for listings enriched at this store, e.g. "GSB-TOR". */
  itemNoPrefix: string;
}

export interface CompanySeed {
  id: number;
  /** CompanyMgmt Company id (HLD §17 `MarketplaceTenant.CompanyId`); mirrors `id` in the mock. */
  companyId: number;
  slug: string;
  name: string;
  shortName: string;
  subdomain: string;
  /** The company pays for its own branded site at `{subdomain}.brijjfinds.com`. */
  ownSiteEnabled: boolean;
  /** Blurb tail - tenant.seed.ts prefixes the derived "N locations · ". */
  blurbRest: string;
  theme: TenantThemeState;
  /** Shipping and returns terms shown before purchase (SHP-03, SM-06). */
  policy: TenantPolicyState;
  stores: CompanyStoreSeed[];
}

/**
 * Platform default under D-08: all sales final except not-as-described. Most
 * retailers run it as-is; the two that compete on convenience override it.
 */
const allSalesFinal = (shippingSummary: string, handlingDays: number, freeShippingThreshold: number | null) => ({
  handlingDays,
  freeShippingThreshold,
  shippingSummary,
  returnWindowDays: 0,
  returnSummary: 'All sales final. Report a not-as-described item within 3 days and we will make it right.'
});

const day = (opens: string, closes: string): StoreDayHours => ({ opens, closes });

/** Thrift pattern: Mon-Sat 9 AM to `closes`, Sunday 10 AM to `sundayCloses`. */
const thriftWeek = (closes: string, sundayCloses: string): StoreWeekHours => [
  day('10:00', sundayCloses),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes)
];

/** ReStore pattern: closed Mondays, Tue-Sat 9 AM to `closes`, Sunday 10 AM-5 PM. */
const restoreWeek = (closes: string): StoreWeekHours => [
  day('10:00', '17:00'),
  null,
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes)
];

/** Boutique pattern: Mon-Fri 11 AM to `closes`, Saturday 10 AM to `closes`, Sunday noon-6 PM. */
const boutiqueWeek = (closes: string): StoreWeekHours => [
  day('12:00', '18:00'),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('11:00', closes),
  day('10:00', closes)
];

/** Sunday-closed thrift pattern: Mon-Sat 9 AM to `closes`. */
const noSundayWeek = (closes: string): StoreWeekHours => [
  null,
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes),
  day('09:00', closes)
];

/**
 * Default marketplace-look theme for companies that have not purchased their
 * own branded site. The avatar color and mission feed the neutral retailer
 * profile and marketplace tiles; the full branded fields (hero, placeholder,
 * accents) activate only if the company later pays (`ownSiteEnabled`).
 */
const marketplaceTheme = (
  logoPrimaryText: string,
  logoSecondaryText: string,
  avatarBg: string,
  missionText: string
): TenantThemeState => ({
  logoPrimaryText,
  logoSecondaryText,
  accent: '#0070b9',
  accentDark: '#005a95',
  avatarBg,
  heroHeadline: 'Great Finds. Local Impact.',
  heroCopy: 'One-of-a-kind secondhand finds, fresh from our local stores every day.',
  searchPlaceholder: 'Search one-of-a-kind finds…',
  missionText,
  defaultLocation: null
});

export const seedCompanies = (): CompanySeed[] => [
  {
    id: 1,
    companyId: 1,
    slug: 'goodwill-south-bay',
    name: 'Goodwill of South Bay',
    shortName: 'Goodwill of South Bay',
    subdomain: 'goodwillsb',
    ownSiteEnabled: false,
    blurbRest: 'Torrance, Long Beach, Carson & more',
    policy: allSalesFinal('Ships in 2 business days from the Torrance sorting floor.', 2, 75),
    theme: marketplaceTheme(
      'goodwill',
      'south bay',
      '#0070b9',
      'Every purchase funds job training and placement programs across the South Bay.'
    ),
    stores: [
      {
        id: 11,
        slug: 'torrance',
        name: 'Goodwill of South Bay - Torrance',
        city: 'Torrance',
        addressLine: '22725 Western Ave',
        zip: '90501',
        lat: 33.8358,
        lng: -118.3406,
        phone: '(310) 555-0114',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.9,
        ratingCount: 1204,
        itemNoPrefix: 'GSB-TOR'
      },
      {
        id: 12,
        slug: 'long-beach',
        name: 'Goodwill of South Bay - Long Beach',
        city: 'Long Beach',
        addressLine: '2610 Atlantic Ave',
        zip: '90806',
        lat: 33.7701,
        lng: -118.1937,
        phone: '(562) 555-0132',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('21:00', '19:00'),
        rating: 4.7,
        ratingCount: 638,
        itemNoPrefix: 'GSB-LGB'
      },
      {
        id: 13,
        slug: 'carson',
        name: 'Goodwill of South Bay - Carson',
        city: 'Carson',
        addressLine: '21827 S Avalon Blvd',
        zip: '90745',
        lat: 33.8317,
        lng: -118.282,
        phone: '(310) 555-0147',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 412,
        itemNoPrefix: 'GSB-CAR'
      },
      {
        id: 14,
        slug: 'san-pedro',
        name: 'Goodwill of South Bay - San Pedro',
        city: 'San Pedro',
        addressLine: '850 S Gaffey St',
        zip: '90731',
        lat: 33.7361,
        lng: -118.2922,
        phone: '(310) 555-0168',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('19:00', '18:00'),
        rating: 4.6,
        ratingCount: 517,
        itemNoPrefix: 'GSB-SNP'
      },
      {
        id: 15,
        slug: 'gardena',
        name: 'Goodwill of South Bay - Gardena',
        city: 'Gardena',
        addressLine: '15505 S Western Ave',
        zip: '90249',
        lat: 33.8884,
        lng: -118.309,
        phone: '(310) 555-0179',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('21:00', '19:00'),
        rating: 4.5,
        ratingCount: 289,
        itemNoPrefix: 'GSB-GAR'
      },
      {
        id: 16,
        slug: 'redondo-beach',
        name: 'Goodwill of South Bay - Redondo Beach',
        city: 'Redondo Beach',
        addressLine: '2318 Artesia Blvd',
        zip: '90278',
        lat: 33.8703,
        lng: -118.3809,
        phone: '(310) 555-0186',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'GSB-RED'
      }
    ]
  },
  {
    id: 2,
    companyId: 2,
    slug: 'habitat-restore-la',
    name: 'Habitat ReStore LA',
    shortName: 'Habitat ReStore LA',
    subdomain: 'habitatla',
    ownSiteEnabled: true,
    blurbRest: 'Pasadena, Burbank, Norwalk, Van Nuys',
    policy: {
      handlingDays: 3,
      freeShippingThreshold: null,
      shippingSummary: 'Furniture and fixtures are pickup only. Smaller goods ship in 3 business days.',
      returnWindowDays: 14,
      returnSummary: 'Return anything except custom or clearance items within 14 days, in original condition.'
    },
    theme: {
      logoPrimaryText: 'habitat',
      logoSecondaryText: 'restore la',
      accent: '#3fa35b',
      accentDark: '#2b7d43',
      avatarBg: '#3fa35b',
      heroHeadline: 'Salvaged today. Someone’s home tomorrow.',
      heroCopy:
        'Doors, dressers, lighting, tile, and one-of-a-kind salvage from our four ReStores across greater LA - ' +
        'donated by builders and neighbors, priced to move, and ready for free store pickup.',
      searchPlaceholder: 'Search the ReStore - "solid oak table", "brass lamp", "french doors"…',
      missionText: 'Proceeds help Habitat for Humanity build affordable homes across Greater Los Angeles.',
      defaultLocation: { label: 'Los Angeles 90012', zip: '90012', lat: 34.0522, lng: -118.2437 }
    },
    stores: [
      {
        id: 21,
        slug: 'pasadena',
        name: 'Habitat ReStore LA - Pasadena',
        city: 'Pasadena',
        addressLine: '770 N Fair Oaks Ave',
        zip: '91103',
        lat: 34.1478,
        lng: -118.1445,
        phone: '(626) 555-0122',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 4 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.8,
        ratingCount: 529,
        itemNoPrefix: 'HRL-PAS'
      },
      {
        id: 22,
        slug: 'burbank',
        name: 'Habitat ReStore LA - Burbank',
        city: 'Burbank',
        addressLine: '1717 W Magnolia Blvd',
        zip: '91506',
        lat: 34.1808,
        lng: -118.309,
        phone: '(818) 555-0139',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 4 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.7,
        ratingCount: 344,
        itemNoPrefix: 'HRL-BUR'
      },
      {
        id: 23,
        slug: 'norwalk',
        name: 'Habitat ReStore LA - Norwalk',
        city: 'Norwalk',
        addressLine: '11900 Firestone Blvd',
        zip: '90650',
        lat: 33.9137,
        lng: -118.0764,
        phone: '(562) 555-0151',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: restoreWeek('19:00'),
        rating: 4.6,
        ratingCount: 402,
        itemNoPrefix: 'HRL-NOR'
      },
      {
        id: 24,
        slug: 'van-nuys',
        name: 'Habitat ReStore LA - Van Nuys',
        city: 'Van Nuys',
        addressLine: '7056 Van Nuys Blvd',
        zip: '91405',
        lat: 34.1975,
        lng: -118.4489,
        phone: '(818) 555-0164',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: restoreWeek('20:00'),
        rating: 4.5,
        ratingCount: 233,
        itemNoPrefix: 'HRL-VAN'
      }
    ]
  },
  {
    id: 3,
    companyId: 3,
    slug: 'arc-thrift-denver',
    name: 'Arc Thrift Denver',
    shortName: 'Arc Thrift Denver',
    subdomain: 'arcdenver',
    ownSiteEnabled: false,
    blurbRest: 'Metro Denver, Lakewood to Aurora',
    policy: allSalesFinal('Ships in 2 business days from Aurora. Free over $50 within Colorado.', 2, 50),
    theme: marketplaceTheme(
      'arc',
      'thrift denver',
      '#7a5af5',
      'Every sale supports advocacy and services for Coloradans with disabilities.'
    ),
    stores: [
      {
        id: 31,
        slug: 'denver',
        name: 'Arc Thrift - Denver',
        city: 'Denver',
        addressLine: '2000 S Broadway',
        zip: '80210',
        lat: 39.7392,
        lng: -104.9903,
        phone: '(303) 555-0117',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('21:00', '18:00'),
        rating: 4.7,
        ratingCount: 861,
        itemNoPrefix: 'ARC-DEN'
      },
      {
        id: 32,
        slug: 'aurora',
        name: 'Arc Thrift - Aurora',
        city: 'Aurora',
        addressLine: '10555 E Colfax Ave',
        zip: '80010',
        lat: 39.7294,
        lng: -104.8319,
        phone: '(303) 555-0129',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.6,
        ratingCount: 298,
        itemNoPrefix: 'ARC-AUR'
      },
      {
        id: 33,
        slug: 'lakewood',
        name: 'Arc Thrift - Lakewood',
        city: 'Lakewood',
        addressLine: '7777 W Colfax Ave',
        zip: '80214',
        lat: 39.7406,
        lng: -105.0838,
        phone: '(303) 555-0143',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('21:00', '18:00'),
        rating: 4.7,
        ratingCount: 654,
        itemNoPrefix: 'ARC-LKW'
      },
      {
        id: 34,
        slug: 'westminster',
        name: 'Arc Thrift - Westminster',
        city: 'Westminster',
        addressLine: '7325 W 88th Ave',
        zip: '80021',
        lat: 39.8617,
        lng: -105.0781,
        phone: '(303) 555-0158',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: 4.5,
        ratingCount: 341,
        itemNoPrefix: 'ARC-WST'
      },
      {
        id: 35,
        slug: 'englewood',
        name: 'Arc Thrift - Englewood',
        city: 'Englewood',
        addressLine: '3455 S Broadway',
        zip: '80113',
        lat: 39.6529,
        lng: -104.9878,
        phone: '(303) 555-0171',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: thriftWeek('19:00', '18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'ARC-ENG'
      }
    ]
  },
  {
    id: 4,
    companyId: 4,
    slug: 'matts-finds',
    name: "Matt's Finds",
    shortName: "Matt's Finds",
    subdomain: 'mattsfinds',
    ownSiteEnabled: true,
    blurbRest: 'Boston · Used hockey gear & collectibles',
    policy: {
      handlingDays: 1,
      freeShippingThreshold: 100,
      shippingSummary: 'Packed and shipped next business day, always tracked and insured.',
      returnWindowDays: 30,
      returnSummary:
        'Thirty-day returns on everything. Gear sizing is unpredictable and we would rather you skated in it.'
    },
    theme: {
      logoPrimaryText: "matt's",
      logoSecondaryText: 'finds',
      accent: '#235ba2',
      accentDark: '#0e3a5c',
      avatarBg: '#235ba2',
      heroHeadline: "Boston's rink-to-rack gear shop.",
      heroCopy:
        "Boston's home for used hockey gear, pro jerseys, and collectible cards. Bid on rare finds, " +
        'buy in one tap, and pick up free at any of our five stores - or have it delivered.',
      searchPlaceholder: 'Search Matt\'s - "Bauer skates", "rookie cards", "goalie pads"…',
      missionText:
        'A slice of every sale funds youth-hockey access programs that put donated gear on Boston kids who need it.',
      defaultLocation: { label: 'Boston 02116', zip: '02116', lat: 42.3503, lng: -71.081 }
    },
    stores: [
      {
        id: 41,
        slug: 'downtown-crossing',
        name: "Matt's Finds - Downtown Crossing",
        city: 'Boston',
        addressLine: '426 Washington St',
        zip: '02108',
        lat: 42.3555,
        lng: -71.0605,
        phone: '(617) 555-0116',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: boutiqueWeek('20:00'),
        rating: 4.9,
        ratingCount: 876,
        itemNoPrefix: 'MTF-BOS'
      },
      {
        id: 42,
        slug: 'cambridge',
        name: "Matt's Finds - Cambridge",
        city: 'Cambridge',
        addressLine: '1105 Massachusetts Ave',
        zip: '02138',
        lat: 42.3736,
        lng: -71.1097,
        phone: '(617) 555-0127',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: boutiqueWeek('20:00'),
        rating: 4.8,
        ratingCount: 542,
        itemNoPrefix: 'MTF-CAM'
      },
      {
        id: 43,
        slug: 'somerville',
        name: "Matt's Finds - Somerville",
        city: 'Somerville',
        addressLine: '240 Elm St, Davis Square',
        zip: '02144',
        lat: 42.3876,
        lng: -71.0995,
        phone: '(617) 555-0138',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.8,
        ratingCount: 431,
        itemNoPrefix: 'MTF-SOM'
      },
      {
        id: 44,
        slug: 'brookline',
        name: "Matt's Finds - Brookline",
        city: 'Brookline',
        addressLine: '318 Harvard St',
        zip: '02446',
        lat: 42.3318,
        lng: -71.1212,
        phone: '(617) 555-0149',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.7,
        ratingCount: 288,
        itemNoPrefix: 'MTF-BRK'
      },
      {
        id: 45,
        slug: 'quincy',
        name: "Matt's Finds - Quincy",
        city: 'Quincy',
        addressLine: '1469 Hancock St',
        zip: '02169',
        lat: 42.2529,
        lng: -71.0023,
        phone: '(617) 555-0157',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: boutiqueWeek('19:00'),
        rating: 4.6,
        ratingCount: 237,
        itemNoPrefix: 'MTF-QUI'
      }
    ]
  },
  {
    id: 5,
    companyId: 5,
    slug: 'second-chance-seattle',
    name: 'Second Chance Seattle',
    shortName: 'Second Chance Seattle',
    subdomain: 'secondchance',
    ownSiteEnabled: false,
    blurbRest: 'Seattle & Tacoma',
    policy: allSalesFinal('Ships in 3 business days from Ballard.', 3, 60),
    theme: marketplaceTheme(
      'second',
      'chance seattle',
      '#3fa35b',
      'Sales fund reentry employment programs that give Seattle neighbors a second chance.'
    ),
    stores: [
      {
        id: 51,
        slug: 'ballard',
        name: 'Second Chance - Ballard',
        city: 'Seattle',
        addressLine: '5423 Ballard Ave NW',
        zip: '98107',
        lat: 47.6768,
        lng: -122.386,
        phone: '(206) 555-0123',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: thriftWeek('20:00', '18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'SCS-SEA'
      },
      {
        id: 52,
        slug: 'capitol-hill',
        name: 'Second Chance - Capitol Hill',
        city: 'Seattle',
        addressLine: '1522 E Olive Way',
        zip: '98122',
        lat: 47.6178,
        lng: -122.3253,
        phone: '(206) 555-0136',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in about an hour',
        weekHours: thriftWeek('21:00', '19:00'),
        rating: 4.8,
        ratingCount: 176,
        itemNoPrefix: 'SCS-CAP'
      },
      {
        id: 53,
        slug: 'tacoma',
        name: 'Second Chance - Tacoma',
        city: 'Tacoma',
        addressLine: '2502 6th Ave',
        zip: '98406',
        lat: 47.2565,
        lng: -122.4626,
        phone: '(253) 555-0148',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 3 hours',
        weekHours: thriftWeek('19:00', '18:00'),
        rating: 4.5,
        ratingCount: 158,
        itemNoPrefix: 'SCS-TAC'
      }
    ]
  },
  {
    id: 6,
    companyId: 6,
    slug: 'st-vincent-thrift',
    name: 'St. Vincent Thrift Phoenix',
    shortName: 'St. Vincent Thrift',
    subdomain: 'stvincent',
    ownSiteEnabled: false,
    blurbRest: 'Metro Phoenix',
    policy: allSalesFinal('Ships in 4 business days. Pickup is same day once staged.', 4, null),
    theme: marketplaceTheme(
      'st. vincent',
      'thrift',
      '#235ba2',
      'Proceeds stock food pantries and emergency-assistance programs across metro Phoenix.'
    ),
    stores: [
      {
        id: 61,
        slug: 'central-phoenix',
        name: 'St. Vincent Thrift - Central Phoenix',
        city: 'Phoenix',
        addressLine: '1218 N Central Ave',
        zip: '85004',
        lat: 33.4484,
        lng: -112.074,
        phone: '(602) 555-0111',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready by end of day',
        weekHours: noSundayWeek('18:00'),
        rating: null,
        ratingCount: null,
        itemNoPrefix: 'SVT-PHX'
      },
      {
        id: 62,
        slug: 'mesa',
        name: 'St. Vincent Thrift - Mesa',
        city: 'Mesa',
        addressLine: '1445 W Southern Ave',
        zip: '85202',
        lat: 33.3928,
        lng: -111.8574,
        phone: '(480) 555-0126',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: noSundayWeek('19:00'),
        rating: 4.6,
        ratingCount: 264,
        itemNoPrefix: 'SVT-MES'
      },
      {
        id: 63,
        slug: 'glendale',
        name: 'St. Vincent Thrift - Glendale',
        city: 'Glendale',
        addressLine: '5750 W Glendale Ave',
        zip: '85301',
        lat: 33.5387,
        lng: -112.1809,
        phone: '(623) 555-0135',
        pickupEnabled: true,
        pickupHoldDays: 7,
        pickupReadyLabel: 'Usually ready in 2 hours',
        weekHours: noSundayWeek('20:00'),
        rating: 4.5,
        ratingCount: 191,
        itemNoPrefix: 'SVT-GLE'
      }
    ]
  }
];
